import {createSelector} from '@reduxjs/toolkit'
import {
  getAnonymousId,
  getDeviceType,
  getLocale,
  getSearchId,
  getUserCity,
  getUserCountryWithFallback,
  getUserCurrencyWithFallback
} from 'modules/meta/selectors'
import {
  getPaymentFlowType,
  getUnifiedOfferCheck
} from 'modules/offerCheck/selectors'
import {getFullStorySessionUrl} from 'services/fullstory'
import {RootState} from 'store'
import {urlParams} from 'utils/urlParams'

import {getBrand} from '@vio/core/src/_web/brand/modules/selectors'
import {getWindowSize} from '@vio/core/src/_web/utils/browser/getWindowSize'
import {
  AnalyticsContext,
  AnalyticsContextList,
  ChargeType,
  type CheckoutOfferContext,
  type HotelContext,
  type LeadContext,
  type PriceContext,
  type SearchContext,
  type SearchOfferContext,
  type TrackEventPayload,
  type TrackEventProperties,
  type UserParametersContext
} from '@vio/core/src/analytics/types/Events'
import {isFlutterAppWebView} from '@vio/core/src/native'

const getEventPayloadParam = (
  _: RootState,
  trackEventPayload: TrackEventPayload
) => trackEventPayload
import getNumberOfNights from 'components/data/URLParams/mappers/getNumberOfNights'
import getRoomConfig from 'components/data/URLParams/mappers/getRoomConfig'
import {
  getAlternativeOffersQueryParams,
  getOffersQuery,
  getRedirectOfferById,
  getRoomsConfigFromQueryParams
} from 'modules/alternativeOffers/selectors'
import {PaymentFlowType} from 'modules/offerCheck/types'
import {getHotelData} from 'modules/searchApi/selectors'

import {getVisitsCount} from '@vio/core/src/_web/userConfig/visitsCount'
import {Price, Source} from '@vio/core/src/api-types/bovio/response/booking'
import {SourceComponentType} from '@vio/core/src/auth/types/Auth'
import {
  getCancellationDeadline,
  getHasFreeCancellation
} from '@vio/core/src/offer/business/cancellationPenalties'
import {BOFH_PROXY_PROVIDER_CODE} from '@vio/core/src/offer/business/isBofhOffer'
import {
  hasChargeLaterPayment,
  hasPayAtPropertyPayment
} from '@vio/core/src/offer/business/offerPaymentMode'
import {getChargeTypes} from '@vio/core/src/offer/business/offers'
import {OfferPrice} from '@vio/core/src/offer/types/offer'
import {
  getChargeableCurrencyPrice,
  getDisplayPrice,
  getPropertyCurrencyHotelFees,
  getTotalAtProperty,
  getTotalPrice,
  getTotalTaxesAndFeesPrice,
  getTotalUpFrontPrice
} from '@vio/core/src/price/business/price'
import {getNightlyDisplayTotalPrice} from '@vio/core/src/room/business/transformSapiRoomsResponse/utils/price'

import getMarketingContext from './contexts/getMarketingContext'
import {getPageName} from './trackEventHelpers'

export const getUserParametersContext = (
  state: RootState
): UserParametersContext => {
  const locale = getLocale(state)
  const userCountry = getUserCountryWithFallback(state)
  const userCity = getUserCity(state)
  const currency = getUserCurrencyWithFallback(state)
  const deviceType = getDeviceType(state)
  const {width: viewPortWidth, height: viewPortHeight} = getWindowSize()
  const fullStorySessionUrl = getFullStorySessionUrl()
  const brand = getBrand(state)

  return {
    deviceType,
    userCountry,
    userCity,
    viewPortWidth,
    viewPortHeight,
    fullStorySessionUrl,
    isNativeAppWebView: isFlutterAppWebView(),
    locale,
    currency,
    visitDayCount: getVisitsCount(),
    brand: brand?.code
  }
}

export const getLeadContext = createSelector(
  [getAnonymousId, getSearchId, getUnifiedOfferCheck],
  (anonymousId, searchId, offerCheckResult): LeadContext => {
    const {checkIn, checkOut, hotelId, providerRateType, redirectId, rooms} =
      urlParams()
    const providerCode = offerCheckResult.data?.providerCode || ''
    return {
      checkIn,
      checkOut,
      hotelId,
      providerCode,
      providerRateType,
      redirectId,
      rooms,
      searchId,
      anonymousId
    }
  }
)
export const getHotelContext = createSelector(
  [getHotelData],
  (hotelData): HotelContext => {
    const {hotelId} = urlParams()
    const securityDeposit = hotelData?.deposit?.value?.amount || null
    const securityDepositCurrency = hotelData?.deposit?.value?.currency || null
    const securityDepositDescription = hotelData?.deposit?.description || null
    const hotelContext: Partial<HotelContext> = {
      hotelId,
      ...(hotelData && {
        securityDeposit,
        securityDepositCurrency,
        hasSecurityDeposit: Boolean(securityDepositDescription)
      })
    }
    return hotelContext as HotelContext
  }
)

export const getSearchContext = createSelector(
  [
    getOffersQuery,
    getAlternativeOffersQueryParams,
    getRoomsConfigFromQueryParams
  ],
  (
    redirectOffers,
    alternativeOffersQueryParams,
    roomsConfig
  ): SearchContext => {
    const {numberOfAdults, numberOfChildren} = roomsConfig
    const {rooms} = alternativeOffersQueryParams
    const {
      searchId = '',
      checkIn = '',
      checkOut = ''
    } = redirectOffers.data || {}
    const searchContext: Partial<SearchContext> = {
      searchId,
      checkIn,
      checkOut,
      searchType: 'hotel',
      searchTrigger: 'pp_landing',
      isUserSearch: false,
      rooms,
      datesType: 'selected',
      numberOfAdults,
      numberOfChildren,
      isOptimizeRooms: false
    }
    return searchContext as SearchContext
  }
)

export const getRedirectOfferContext = createSelector(
  [getRedirectOfferById, getRoomsConfigFromQueryParams],
  (offer, roomsConfig): SearchOfferContext | null => {
    if (!offer) return null

    const {numberOfRooms} = roomsConfig
    const {
      id: offerId,
      osoOfferPosition: offerPosition,
      providerCode,
      intermediaryProvider: proxyProviderCode,
      rate,
      cancellationPenalties,
      // we don't carry showTotalPrices param to checkout page, therefore nightly rates are always displayed
      nightlyRate: displayPrice
    } = offer
    const hasFreeCancellation = getHasFreeCancellation(cancellationPenalties)
    const canPayLater =
      hasPayAtPropertyPayment(offer) || hasChargeLaterPayment(offer)
    const rateBreakdown = {
      baseRate: rate.base,
      localTaxes: rate.hotelFees,
      taxes: rate.taxes
    }
    const offerContext: Partial<SearchOfferContext> = {
      offerId,
      offerPosition,
      providerCode,
      proxyProviderCode,
      numberOfRooms,
      hasFreeCancellation,
      canPayLater,
      displayPrice,
      rateBreakdown,
      sourceComponent: SourceComponentType.PaymentPageAlternativeOffersOverlay
    }
    return offerContext as SearchOfferContext
  }
)

export const getRedirectOffersContexts = createSelector(
  [getSearchContext, getRedirectOfferContext],
  (searchContext, offerContext): AnalyticsContextList => ({
    [AnalyticsContext.SearchContext]: searchContext,
    [AnalyticsContext.OfferContext]: offerContext
  })
)

export const getOfferContext = createSelector(
  [getUnifiedOfferCheck, getPaymentFlowType],
  ({data: offerCheck}, flowType): CheckoutOfferContext | null => {
    const {
      offer_token,
      offerId = '',
      roomId: urlRoomId,
      firstOfferTk,
      bundleId
    } = urlParams()

    const isSplitBooking = flowType == PaymentFlowType.SplitBooking

    const originalOfferToken =
      (isSplitBooking ? firstOfferTk : offer_token) || null

    if (!offerCheck) {
      // Providing fallback context if offer is not available
      return {
        bookURI: '',
        cancellationPolicy: {
          freeRefundableUntil: null,
          description: null
        },
        canPayLater: false,
        chargeType: [ChargeType.Default],
        displayPricePerRoomPerNight: 0,
        hasFreeCancellation: false,
        meals: [],
        offerId,
        offerPosition: null,
        providerCode: '',
        providerFeedId: null, // Attach to the context when providerFeedId is available via SAPI rooms()
        proxyProviderCode: BOFH_PROXY_PROVIDER_CODE,
        roomId: urlRoomId || '',
        roomName: '',
        totalPrice: 0,
        offerToken: null,
        originalOfferToken,
        isSplitBooking,
        splitBooking: null,
        bundleId: bundleId || null
      }
    }

    const {
      chargeDueAt,
      bookingInfo,
      providerCode,
      rooms,
      prices = [],
      cancellationPenalties = [],
      links = [],
      token: offerToken = null
    } = offerCheck
    const displayPricePerRoomPerNight = getNightlyDisplayTotalPrice(prices)

    const price = getDisplayPrice(prices)
    const cancellationDeadline = getCancellationDeadline(cancellationPenalties)
    const hasFreeCancellation = getHasFreeCancellation(cancellationPenalties)
    const offerCheckRoom = rooms?.[0]

    const isChargeLaterEligible = hasChargeLaterPayment(offerCheck)

    const totalPrice = getTotalPrice(price)

    const chargeTypes = getChargeTypes({
      isExistingBooking: !!bookingInfo,
      isChargeLaterEligible,
      isPayAtProperty: hasPayAtPropertyPayment(offerCheck)
    })

    return {
      bookURI: links[0]?.href,
      cancellationPolicy: {
        freeRefundableUntil: cancellationDeadline || null,
        description: null // not relevant for tracking
      },
      canPayLater:
        hasPayAtPropertyPayment(offerCheck) ||
        isChargeLaterEligible ||
        bookingInfo?.source === Source.ChargeLater,
      chargeType: chargeTypes,
      displayPricePerRoomPerNight,
      hasFreeCancellation,
      meals: offerCheckRoom?.mealPlan?.map(mp => mp.meal) ?? [],
      offerId,
      providerCode: providerCode || '',
      providerFeedId: null, // Attach to the context when providerFeedId is available via SAPI rooms()
      proxyProviderCode: BOFH_PROXY_PROVIDER_CODE,
      roomId: offerCheckRoom?.id || '',
      roomMasterId: offerCheckRoom?.masterId || '',
      roomName: offerCheckRoom?.name || '',
      totalPrice,
      // for SB case falls back to originalOfferToken, since offerToken is null
      offerToken: offerToken || originalOfferToken,
      originalOfferToken,
      isSplitBooking,
      splitBooking: isSplitBooking ? offerCheck : null,
      bundleId: bundleId || null,
      ...(isChargeLaterEligible &&
        chargeDueAt && {
          paymentExpiryAt: {
            chargeExpiryAt: chargeDueAt
          }
        })
    }
  }
)

export const getPriceContext = createSelector(
  [getUnifiedOfferCheck],
  ({data: offerCheck}): PriceContext | null => {
    const params = urlParams()
    const {offerId = ''} = params
    const {numberOfRooms: rooms} = getRoomConfig(params)

    if (!offerCheck) return null
    const {checkIn, checkOut} = offerCheck
    const nights = getNumberOfNights({checkIn, checkOut})

    const canPayLater = hasPayAtPropertyPayment(offerCheck)

    const price = getDisplayPrice<OfferPrice | Price>(offerCheck.prices)

    const currency = price?.chargeable.currency
    const totalBasePrice = Number(price?.chargeable.base)
    const displayPricePerRoomPerNight = getNightlyDisplayTotalPrice(
      offerCheck?.prices
    )
    const totalUpFront = getTotalUpFrontPrice(price, canPayLater)
    const totalPrice = getTotalPrice(price)

    const totalDueAtProperty = getTotalAtProperty(price, canPayLater)

    const chargeableCurrencyPrice = getChargeableCurrencyPrice(
      offerCheck?.prices
    )
    const chargeableCurrency = chargeableCurrencyPrice?.currencyCode

    const chargeableTotalPrice = getTotalPrice(chargeableCurrencyPrice)
    const totalTaxesAndFees = getTotalTaxesAndFeesPrice(price)
    const {currencyCode: propertyCurrencyCode} =
      getPropertyCurrencyHotelFees(offerCheck?.prices) || {}

    const taxesAndFees = price
      ? [
          ...('taxes' in price && price?.taxes?.breakdown
            ? price.taxes.breakdown
            : []
          ).map(item => ({
            ...item,
            total: Number(item.total),
            isPayAtProperty: false
          })),
          ...(price?.hotelFees?.breakdown || []).map(item => ({
            ...item,
            total: Number(item.total),
            isPayAtProperty: true
          }))
        ]
      : []
    return {
      // offer params
      offerId,
      rooms,
      nights,
      // price params
      currency,
      totalBasePrice,
      displayPricePerRoomPerNight,
      totalUpFront,
      totalPrice,
      totalDueAtProperty,
      chargeableCurrency,
      chargeableTotalPrice,
      propertyCurrencyCode,
      totalTaxesAndFees,
      taxesAndFees: taxesAndFees || []
    }
  }
)

export const getAnalyticsContext = createSelector(
  [
    getOfferContext,
    getPriceContext,
    getLeadContext,
    getHotelContext,
    getUserParametersContext
  ],
  (
    offerContext,
    priceContext,
    leadContext,
    hotelContext,
    userParametersContext
  ) => ({
    [AnalyticsContext.OfferContext]: offerContext,
    [AnalyticsContext.PriceContext]: priceContext,
    [AnalyticsContext.LeadContext]: leadContext,
    [AnalyticsContext.MarketingContext]: getMarketingContext(),
    [AnalyticsContext.UserParametersContext]: userParametersContext,
    [AnalyticsContext.HotelContext]: hotelContext
  })
)

export const getEventProperties = createSelector(
  [getEventPayloadParam, getAnalyticsContext],
  (
    trackEventPayload,
    defaultAnalyticsContext
  ): TrackEventProperties & {name: string} => ({
    ...trackEventPayload,
    name: `${trackEventPayload.category}_${trackEventPayload.entity}_${trackEventPayload.action}`,
    page: trackEventPayload.page ?? getPageName(window.location.pathname),
    analyticsContext: {
      ...defaultAnalyticsContext,
      ...trackEventPayload.analyticsContext
    }
  })
)
